import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { io } from 'socket.io-client';
import L from 'leaflet';

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
  LayersControl
} from 'react-leaflet';

import 'leaflet/dist/leaflet.css';

const SOCKET_URL = 'https://eapi.octaloptimum.in';

// A visible animated officer marker.
const officerIcon = L.divIcon({
  className: 'officer-live-marker',
  html: `
    <div class="officer-marker-wrapper">
      <div class="officer-marker-pulse"></div>
      <div class="officer-marker-dot">
        <span>👮</span>
      </div>
    </div>
  `,
  iconSize: [48, 48],
  iconAnchor: [24, 24]
});

function getOfficerIds(officer) {
  return [
    officer?._id,
    officer?.userId,
    officer?.officerId,
    officer?.officerCode
  ]
    .filter(value => value != null && value !== '')
    .map(value => String(value).trim());
}

function getCoordinates(officer) {
  const lat = Number(
    officer?.liveLat ??
    officer?.currentLocation?.lat ??
    officer?.location?.lat ??
    officer?.latitude
  );

  const lng = Number(
    officer?.liveLng ??
    officer?.currentLocation?.lng ??
    officer?.location?.lng ??
    officer?.longitude
  );

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  // Reject the common default/uninitialized coordinate pair.
  if (lat === 0 && lng === 0) return null;

  return [lat, lng];
}

function calculateDistance(lat1, lon1, lat2, lon2) {
  if (
    lat1 == null ||
    lon1 == null ||
    lat2 == null ||
    lon2 == null
  ) {
    return null;
  }

  const toRad = value => value * Math.PI / 180;
  const R = 6371000;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) ** 2;

  return R * 2 * Math.atan2(
    Math.sqrt(a),
    Math.sqrt(1 - a)
  );
}
function FollowOfficer({ position, enabled }) {
  const map = useMap();

  useEffect(() => {
    if (!position || !enabled) return;

    map.panTo(position, {
      animate: true,
      duration: 0.8
    });

  }, [position, enabled, map]);

  return null;
}

export default function LiveOfficerMap({
  initialOfficer,
  token
}) {
  const [officer, setOfficer] = useState(initialOfficer || null);
  const [trail, setTrail] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const [lastSocketUpdate, setLastSocketUpdate] = useState(null);
  const [followEnabled, setFollowEnabled] = useState(true);
  // Keep the latest officer available to the socket listener
  // without reconnecting the socket on every GPS update.
  const officerRef = useRef(initialOfficer || null);

  useEffect(() => {
    officerRef.current = officer;
  }, [officer]);

  // Update selected officer when the API data arrives.
  useEffect(() => {
    setOfficer(initialOfficer || null);
    officerRef.current = initialOfficer || null;

    const initialPosition = getCoordinates(initialOfficer);

    setTrail(initialPosition ? [initialPosition] : []);
  }, [initialOfficer]);

  // One socket connection for this map page.
  useEffect(() => {
    if (!token) {
      console.error('Live map: authentication token is missing.');
      return;
    }

    if (!initialOfficer) {
      console.error('Live map: initial officer data is missing.');
      return;
    }

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000
    });

    socket.on('connect', () => {
      console.log('LIVE MAP SOCKET CONNECTED:', socket.id);
      setSocketConnected(true);
    });

    socket.on('connect_error', error => {
      console.error('LIVE MAP SOCKET ERROR:', error.message);
      setSocketConnected(false);
    });

    socket.on('disconnect', reason => {
      console.log('LIVE MAP SOCKET DISCONNECTED:', reason);
      setSocketConnected(false);
    });

    socket.on('officer:location', payload => {
      console.log('LIVE MAP LOCATION EVENT:', payload);

      // Supports either a direct payload or a payload wrapped in data.
      const location = payload?.data || payload;

      const eventOfficerId = String(
        location?.officerId ??
        location?.userId ??
        location?.user?._id ??
        ''
      ).trim();

      const lat = Number(location?.lat ?? location?.latitude);
      const lng = Number(location?.lng ?? location?.longitude);

      if (
        !eventOfficerId ||
        !Number.isFinite(lat) ||
        !Number.isFinite(lng) ||
        lat < -90 ||
        lat > 90 ||
        lng < -180 ||
        lng > 180 ||
        (lat === 0 && lng === 0)
      ) {
        console.warn('Invalid officer location event:', location);
        return;
      }

      const currentOfficer = officerRef.current;

      if (!currentOfficer) return;

      const selectedOfficerIds = getOfficerIds(currentOfficer);

      console.log('Selected officer IDs:', selectedOfficerIds);
      console.log('Socket event officer ID:', eventOfficerId);

      if (!selectedOfficerIds.includes(eventOfficerId)) {
        console.warn(
          'Location event ignored: officer ID does not match selected officer.'
        );
        return;
      }

      const boothLat = Number(currentOfficer.boothLat);
      const boothLng = Number(currentOfficer.boothLng);

      const distance =
        Number.isFinite(boothLat) && Number.isFinite(boothLng)
          ? calculateDistance(lat, lng, boothLat, boothLng)
          : null;

      const radius = Number(currentOfficer.geofenceRadius || 75);

      const updatedOfficer = {
        ...currentOfficer,
        liveLat: lat,
        liveLng: lng,
        distanceMeters:
          distance == null
            ? currentOfficer.distanceMeters
            : Math.round(distance),
        lastPing:
          location.updatedAt ||
          location.timestamp ||
          new Date().toISOString(),
        isOnline: true,
        isOffline: false,
        status:
          distance == null
            ? currentOfficer.status
            : distance <= radius
              ? 'AT_STATION'
              : 'AWAY'
      };

      officerRef.current = updatedOfficer;
      setOfficer(updatedOfficer);

      setLastSocketUpdate(new Date().toLocaleTimeString());

      setTrail(previous => {
        const next = [...previous, [lat, lng]];
        return next.slice(-300);
      });
    });

    return () => {
      socket.off('connect');
      socket.off('connect_error');
      socket.off('disconnect');
      socket.off('officer:location');
      socket.disconnect();
    };
  }, [token, initialOfficer]);

  const position = useMemo(
    () => getCoordinates(officer),
    [
      officer?.liveLat,
      officer?.liveLng,
      officer?.currentLocation?.lat,
      officer?.currentLocation?.lng,
      officer?.location?.lat,
      officer?.location?.lng,
      officer?.latitude,
      officer?.longitude
    ]
  );

  if (!officer) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-950 text-white">
        Officer not found.
      </div>
    );
  }

  return (
    <div className="live-officer-map-container h-screen flex flex-col bg-slate-950">

      <style>
        {`
    .officer-live-marker {
      background: transparent !important;
      border: none !important;
    }

    .officer-marker-wrapper {
      position: relative;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .officer-marker-pulse {
      position: absolute;
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: rgba(22, 163, 74, 0.25);
      border: 2px solid #16a34a;
      animation: officerPulse 1.5s ease-out infinite;
    }

    .officer-marker-dot {
      position: relative;
      z-index: 2;
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: #16a34a;
      border: 3px solid white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 17px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.5);
    }

    @keyframes officerPulse {
      0% {
        transform: scale(0.7);
        opacity: 0.9;
      }
      100% {
        transform: scale(1.6);
        opacity: 0;
      }
    }
  `}
      </style>

      <div className="bg-slate-900 text-white p-4 flex flex-wrap justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">
            {officer.name || officer.displayName || 'Police Officer'}
          </h2>

          <p className="text-sm text-slate-400">
            {officer.designation || '-'} ·{' '}
            {officer.officerCode || officer.officerId || '-'}
          </p>

          <p className="text-xs text-slate-400 mt-1">
            {officer.policeStationName || 'Police Station unavailable'}
            {' · '}
            {officer.boothName || 'Booth unavailable'}
          </p>
        </div>

        <div className="flex flex-col items-end gap-2">
          <span
            className={
              socketConnected
                ? 'text-emerald-400 font-bold'
                : 'text-amber-400 font-bold'
            }
          >
            {socketConnected ? '● LIVE CONNECTION' : '● DISCONNECTED'}
          </span>

          <span className="text-sm font-bold">
            {officer.status || 'LOCATION UNKNOWN'}
          </span>

          {lastSocketUpdate && (
            <span className="text-xs text-slate-400">
              Socket update: {lastSocketUpdate}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 bg-slate-900 text-white">
        <div>
          <p className="text-xs text-slate-400">GPS Latitude</p>
          <strong>{position?.[0]?.toFixed(6) ?? '--'}</strong>
        </div>

        <div>
          <p className="text-xs text-slate-400">GPS Longitude</p>
          <strong>{position?.[1]?.toFixed(6) ?? '--'}</strong>
        </div>

        <div>
          <p className="text-xs text-slate-400">Distance from Booth</p>
          <strong>
            {officer.distanceMeters != null
              ? `${officer.distanceMeters} m`
              : '--'}
          </strong>
        </div>

        <div>
          <p className="text-xs text-slate-400">Last GPS Update</p>
          <strong className="text-xs">
            {officer.lastPing
              ? new Date(officer.lastPing).toLocaleTimeString()
              : '--'}
          </strong>
        </div>
      </div>

      <div className="flex-1 min-h-0 relative">

        {position ? (
          <>
            <MapContainer
              center={position}
              zoom={17}
              minZoom={3}
              maxZoom={20}
              zoomControl={true}
              scrollWheelZoom={true}
              doubleClickZoom={true}
              touchZoom={true}
              dragging={true}
              className="w-full h-full"
            >

              <LayersControl position="topright">

                {/* NORMAL MAP */}
                <LayersControl.BaseLayer checked name="Normal Map">
                  <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>

                {/* SATELLITE MAP */}
                <LayersControl.BaseLayer name="Satellite">
                  <TileLayer
                    attribution="Tiles &copy; Esri"
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>

                {/* HYBRID SATELLITE */}
                <LayersControl.BaseLayer name="Hybrid Satellite">
                  <TileLayer
                    attribution="Tiles &copy; Esri"
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={19}
                  />

                </LayersControl.BaseLayer>

              </LayersControl>

              {/* Automatic following */}
              <FollowOfficer
                position={position}
                enabled={followEnabled}
              />

              {/* Officer movement trail */}
              {trail.length > 1 && (
                <Polyline
                  positions={trail}
                  pathOptions={{
                    color: '#2563eb',
                    weight: 4,
                    opacity: 0.8
                  }}
                />
              )}

              {/* Officer marker */}
              <Marker
                position={position}
                icon={officerIcon}
              >
                <Popup>
                  <strong>
                    {officer.name || officer.displayName || 'Police Officer'}
                  </strong>
                  <br />
                  {officer.designation || '-'}
                  <br />
                  {officer.status || 'UNKNOWN'}
                  <br />
                  {position[0].toFixed(6)}, {position[1].toFixed(6)}
                </Popup>
              </Marker>

            </MapContainer>

            {/* Follow button */}
            <button
              type="button"
              onClick={() => setFollowEnabled(value => !value)}
              className={`absolute bottom-6 right-4 z-[1000] px-4 py-2 rounded-lg shadow-lg font-semibold ${followEnabled
                ? 'bg-emerald-600 text-white'
                : 'bg-white text-slate-800'
                }`}
            >
              {followEnabled ? 'Following Officer' : 'Follow Officer'}
            </button>
          </>
        ) : (
          <div className="h-full flex items-center justify-center text-white">
            No valid GPS coordinates available for this officer.
          </div>
        )}

      </div>

    </div>
  );
}